import { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from './useApi';

const useBulkOperations = () => {
  const { isAuthenticated } = useAuth();
  const { apiCall } = useApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Modifier le statut de plusieurs utilisateurs
  const updateUsersStatus = useCallback(async (userIds, newStatus) => {
    if (!isAuthenticated) throw new Error('Non authentifié');

    try {
      setLoading(true);
      setError(null);

      const response = await apiCall('/admin/bulk/users/status', {
        method: 'POST',
        body: JSON.stringify({
          user_ids: userIds,
          new_status: newStatus
        })
      });

      return response;
    } catch (err) {
      console.error('Erreur lors de la mise à jour en lot des statuts:', err);
      setError('Erreur lors de la mise à jour des statuts');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, apiCall]);

  // Supprimer plusieurs utilisateurs
  const deleteUsers = useCallback(async (userIds, confirm = false) => {
    if (!isAuthenticated) throw new Error('Non authentifié');

    try {
      setLoading(true);
      setError(null);

      const response = await apiCall('/admin/bulk/users/delete', {
        method: 'POST',
        body: JSON.stringify({
          user_ids: userIds,
          confirm
        })
      });

      return response;
    } catch (err) {
      console.error('Erreur lors de la suppression en lot:', err);
      setError('Erreur lors de la suppression des utilisateurs');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, apiCall]);

  // Envoyer une notification à plusieurs utilisateurs
  const sendBulkNotifications = useCallback(async (userIds, title, message, type = 'system', data = {}) => {
    if (!isAuthenticated) throw new Error('Non authentifié');

    try {
      setLoading(true);
      setError(null);

      const response = await apiCall('/admin/bulk/notifications/send', {
        method: 'POST',
        body: JSON.stringify({
          user_ids: userIds,
          title,
          message,
          type,
          data
        })
      });

      return response;
    } catch (err) {
      console.error('Erreur lors de l\'envoi en lot de notifications:', err);
      setError('Erreur lors de l\'envoi des notifications');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, apiCall]);

  // Exporter des données en lot
  const exportData = useCallback(async (entityType, filters = {}, format = 'csv', fields = null) => {
    if (!isAuthenticated) throw new Error('Non authentifié');

    try {
      setLoading(true);
      setError(null);

      const response = await apiCall('/admin/bulk/export', {
        method: 'POST',
        body: JSON.stringify({
          entity_type: entityType,
          filters,
          format,
          fields
        })
      });

      return response;
    } catch (err) {
      console.error('Erreur lors de l\'export en lot:', err);
      setError('Erreur lors de l\'export des données');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, apiCall]);

  // Récupérer l'historique des opérations en lot
  const fetchOperationsHistory = useCallback(async (page = 1, limit = 20) => {
    if (!isAuthenticated) throw new Error('Non authentifié');

    try {
      setLoading(true);
      setError(null);

      const response = await apiCall(`/admin/bulk/operations?page=${page}&limit=${limit}`);
      return response;
    } catch (err) {
      console.error('Erreur lors de la récupération de l\'historique:', err);
      setError('Erreur lors du chargement de l\'historique');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, apiCall]);

  // Utilitaires pour la validation
  const validateUserIds = useCallback((userIds) => {
    if (!Array.isArray(userIds) || userIds.length === 0) {
      throw new Error('Liste d\'identifiants requise');
    }

    const maxUsers = 1000;
    if (userIds.length > maxUsers) {
      throw new Error(`Maximum ${maxUsers} éléments par opération`);
    }

    return true;
  }, []);

  const validateNotificationData = useCallback((title, message, userIds) => {
    if (!title || !message) {
      throw new Error('Titre et message requis');
    }

    if (title.length > 255) {
      throw new Error('Le titre ne peut pas dépasser 255 caractères');
    }

    if (message.length > 1000) {
      throw new Error('Le message ne peut pas dépasser 1000 caractères');
    }

    validateUserIds(userIds);
    return true;
  }, [validateUserIds]);

  const validateExportParams = useCallback((entityType, format) => {
    const supportedEntities = ['users', 'clients', 'prestataires', 'missions', 'transactions'];
    const supportedFormats = ['csv', 'json', 'xlsx'];

    if (!supportedEntities.includes(entityType)) {
      throw new Error('Type d\'entité non supporté');
    }

    if (!supportedFormats.includes(format)) {
      throw new Error('Format non supporté');
    }

    return true;
  }, []);

  return {
    // État
    loading,
    error,
    
    // Opérations principales
    updateUsersStatus,
    deleteUsers,
    sendBulkNotifications,
    exportData,
    fetchOperationsHistory,
    
    // Validation
    validateUserIds,
    validateNotificationData,
    validateExportParams,
    
    // Utils
    isAuthenticated
  };
};

export default useBulkOperations;